import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Plural, Trans } from "@lingui/react/macro"
import { Alert, Badge, Center, Container, Group, Stack, Text, Title } from "@mantine/core"
import { IconAlertTriangle } from "@tabler/icons-react"
import { useMemo } from "react"
import { DetailSkeleton, TableSkeleton } from "../../../../components/PageSkeletons"
import { PipelineGraph } from "../../../../components/PipelineGraph"
import type { IStepRunStatus } from "../../../../components/PipelineGraph/interfaces"
import { formatDateTime } from "../../../../lib/format-date"
import { buildRunStatusMap, formatDurationMs, formatRunWhen, groupStepsByWave } from "./helpers"
import type {
  IPipelineRunDetail,
  IPipelineRunStep,
  IPipelineRunSummary,
  IProps,
  TPipelineRunStatus,
} from "./interfaces"
import classes from "./styles.module.css"

function MeetingLabel({
  run,
}: {
  run: Pick<IPipelineRunSummary, "companyName" | "meetingStartedAt" | "meetingDurationMinutes">
}): React.JSX.Element {
  const { i18n } = useLingui()
  const company = run.companyName && run.companyName.trim() !== "" ? run.companyName : i18n._(msg`Unknown company`)
  const when = formatDateTime(i18n, run.meetingStartedAt)
  const minutes = run.meetingDurationMinutes
  return (
    <>
      {company}
      {when !== "" ? <> · {when}</> : null}
      {minutes !== null && minutes > 0 ? (
        <>
          {" · "}
          <Plural value={minutes} one="# minute" other="# minutes" />
        </>
      ) : null}
    </>
  )
}

function RunStatusBadge({ status }: { status: TPipelineRunStatus }): React.JSX.Element {
  if (status === "complete") {
    return (
      <Badge color="success" variant="light" size="sm" className={classes.statusBadge}>
        <Trans>Complete</Trans>
      </Badge>
    )
  }
  if (status === "failed") {
    return (
      <Badge color="danger" variant="light" size="sm" className={classes.statusBadge}>
        <Trans>Failed</Trans>
      </Badge>
    )
  }
  return (
    <Badge color="info" variant="light" size="sm" className={classes.statusBadge}>
      <Trans>Running</Trans>
    </Badge>
  )
}

function StepStatusBadge({ status }: { status: IStepRunStatus["status"] }): React.JSX.Element {
  if (status === "done") {
    return (
      <Badge color="success" variant="light" size="xs">
        <Trans>Done</Trans>
      </Badge>
    )
  }
  if (status === "failed") {
    return (
      <Badge color="danger" variant="light" size="xs">
        <Trans>Failed</Trans>
      </Badge>
    )
  }
  if (status === "running") {
    return (
      <Badge color="info" variant="light" size="xs">
        <Trans>Running</Trans>
      </Badge>
    )
  }
  if (status === "skipped") {
    return (
      <Badge color="gray" variant="light" size="xs">
        <Trans>Skipped</Trans>
      </Badge>
    )
  }
  return (
    <Badge color="gray" variant="outline" size="xs">
      <Trans>Queued</Trans>
    </Badge>
  )
}

function findStep(detail: IPipelineRunDetail | null, nodeId: string | null): IPipelineRunStep | null {
  if (!detail || !nodeId) return null
  return detail.steps.find((s) => s.nodeId === nodeId) ?? null
}

function findNodeName(detail: IPipelineRunDetail | null, nodeId: string): string {
  const fromGraph = detail?.graph?.nodes.find((n) => n.id === nodeId)?.name
  return fromGraph ?? nodeId
}

function RunsTable({
  runs,
  selectedRunId,
  onSelectRun,
}: {
  runs: IPipelineRunSummary[]
  selectedRunId: string | null
  onSelectRun: (runId: string | null) => void
}): React.JSX.Element {
  return (
    <div className={classes.tableWrap}>
      <table className={classes.table} data-testid="pipeline-runs-table">
        <thead>
          <tr>
            <th>
              <Trans>Meeting</Trans>
            </th>
            <th>
              <Trans>Template</Trans>
            </th>
            <th>
              <Trans>Status</Trans>
            </th>
            <th>
              <Trans>Duration</Trans>
            </th>
            <th>
              <Trans>Slowest</Trans>
            </th>
            <th>
              <Trans>Failed</Trans>
            </th>
            <th>
              <Trans>When</Trans>
            </th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => (
            <tr
              key={run.id}
              className={classes.row}
              data-selected={selectedRunId === run.id ? "true" : undefined}
              data-testid={`pipeline-run-row-${run.id}`}
              onClick={() => onSelectRun(run.id === selectedRunId ? null : run.id)}
            >
              <td className={classes.meetingCell} title={run.meetingId}>
                <MeetingLabel run={run} />
              </td>
              <td className={classes.templateCell} title={run.template}>
                {run.template}
              </td>
              <td className={classes.statusCell}>
                <RunStatusBadge status={run.status} />
              </td>
              <td className={classes.durationCell}>{formatDurationMs(run.totalDurationMs)}</td>
              <td className={classes.slowestCell}>
                {run.slowestStep ? (
                  <span className={classes.mono}>
                    {run.slowestStep.nodeId} · {formatDurationMs(run.slowestStep.durationMs)}
                  </span>
                ) : (
                  "—"
                )}
              </td>
              <td className={classes.failedCell}>{run.failedStepCount}</td>
              <td className={classes.whenCell}>{formatRunWhen(run.startedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function NodeDrillDown({ detail, nodeId }: { detail: IPipelineRunDetail; nodeId: string }): React.JSX.Element {
  const step = findStep(detail, nodeId)
  const name = findNodeName(detail, nodeId)

  return (
    <div className={classes.nodePanel} data-testid="pipeline-node-detail">
      <Text className={classes.nodePanelTitle}>{name}</Text>
      <Text className={classes.nodePanelMeta}>{nodeId}</Text>
      {step ? (
        <Stack gap={4} mt="xs">
          <Group gap="xs">
            <StepStatusBadge status={step.status} />
            <Text size="sm" c="dimmed">
              {formatDurationMs(step.durationMs)}
            </Text>
          </Group>
          {step.error ? (
            <Text className={classes.nodePanelError} data-testid="pipeline-node-error">
              {step.error}
            </Text>
          ) : null}
        </Stack>
      ) : (
        <Text size="sm" c="dimmed" mt="xs">
          <Trans>No step status recorded for this node.</Trans>
        </Text>
      )}
    </div>
  )
}

function WaveFallbackList({
  steps,
  selectedNodeId,
  onSelectNode,
}: {
  steps: IPipelineRunStep[]
  selectedNodeId: string | null
  onSelectNode: (nodeId: string | null) => void
}): React.JSX.Element {
  const groups = useMemo(() => groupStepsByWave(steps), [steps])

  return (
    <div className={classes.waveList} data-testid="pipeline-wave-fallback">
      <Text size="sm" c="dimmed">
        <Trans>Template topology is unavailable for this run. Showing steps grouped by wave.</Trans>
      </Text>
      {groups.map((group) => (
        <div key={group.wave} className={classes.waveGroup}>
          <div className={classes.waveTitle}>
            <Trans>Wave {group.wave}</Trans>
          </div>
          {group.steps.map((step) => (
            <button
              key={step.nodeId}
              type="button"
              className={classes.waveStep}
              data-selected={selectedNodeId === step.nodeId ? "true" : undefined}
              onClick={() => onSelectNode(step.nodeId === selectedNodeId ? null : step.nodeId)}
            >
              <span className={classes.waveStepId}>{step.nodeId}</span>
              <span className={classes.waveStepMeta}>
                <StepStatusBadge status={step.status} />
                {formatDurationMs(step.durationMs)}
              </span>
            </button>
          ))}
        </div>
      ))}
    </div>
  )
}

function SelectedRunDetail({
  detail,
  isLoading,
  isError,
  errorMessage,
  selectedNodeId,
  onSelectNode,
}: {
  detail: IPipelineRunDetail | null
  isLoading: boolean
  isError: boolean
  errorMessage: string | null
  selectedNodeId: string | null
  onSelectNode: (nodeId: string | null) => void
}): React.JSX.Element {
  if (isError) {
    return (
      <Alert color="danger" icon={<IconAlertTriangle size={16} />}>
        <Trans>Couldn’t load this run.</Trans>
        {errorMessage ? (
          <Text size="sm" mt={4}>
            {errorMessage}
          </Text>
        ) : null}
      </Alert>
    )
  }

  if (isLoading && !detail) {
    return <DetailSkeleton />
  }

  if (!detail) {
    return (
      <div className={classes.emptyDetail}>
        <Text c="dimmed">
          <Trans>Select a run to inspect its DAG and step timings.</Trans>
        </Text>
      </div>
    )
  }

  const runStatus = buildRunStatusMap(detail.steps)
  const graph = detail.graph

  return (
    <div className={classes.detailPanel} data-testid="pipeline-run-detail">
      <div className={classes.detailHeader}>
        <div>
          <Group gap="xs" mb={4}>
            <RunStatusBadge status={detail.run.status} />
            {detail.run.status === "running" ? (
              <span className={classes.liveCue} data-testid="pipeline-run-live-cue">
                <span className={classes.liveDot} data-tone="info" aria-hidden />
                <Text size="xs" component="span" c="dimmed">
                  <Trans>Live</Trans>
                </Text>
              </span>
            ) : null}
            <Text size="sm" c="dimmed">
              {detail.run.template}
            </Text>
          </Group>
          <Text size="xs" className={classes.mono} c="dimmed">
            {detail.run.id}
          </Text>
        </div>
        <Text size="xs" c="dimmed">
          <MeetingLabel run={detail.run} />
          {" · "}
          <Plural value={detail.steps.length} one="# step" other="# steps" />
        </Text>
      </div>

      {graph ? (
        <PipelineGraph
          nodes={graph.nodes}
          edges={graph.edges}
          waveCount={graph.waveCount}
          runStatus={runStatus}
          selectedNodeId={selectedNodeId}
          onNodeSelect={onSelectNode}
        />
      ) : (
        <WaveFallbackList steps={detail.steps} selectedNodeId={selectedNodeId} onSelectNode={onSelectNode} />
      )}

      {selectedNodeId ? <NodeDrillDown detail={detail} nodeId={selectedNodeId} /> : null}
    </div>
  )
}

/**
 * Dumb super-admin surface for live pipeline-run observability. Receives the
 * runs list + selected-run detail as props; the smart route owns fetching,
 * selection state, and the super-admin guard.
 */
export const PipelineRunsView = ({
  runs,
  isLoading,
  isError,
  errorMessage,
  selectedRunId,
  selectedDetail,
  isDetailLoading,
  isDetailError,
  detailErrorMessage,
  onSelectRun,
  selectedNodeId,
  onSelectNode,
}: IProps): React.JSX.Element => {
  const { i18n } = useLingui()

  const header = (
    <div>
      <Title order={2}>
        <Trans>Pipeline runs</Trans>
      </Title>
      <Text c="dimmed" size="sm">
        <Trans>
          Recent finalize runs across all tenants. Select a run to see step status on the DAG and drill into a node’s
          duration or error.
        </Trans>
      </Text>
    </div>
  )

  if (isError) {
    return (
      <Container size="xl" py="md" className={classes.page}>
        <Stack gap="lg">
          {header}
          <Alert color="danger" icon={<IconAlertTriangle size={16} />} title={i18n._(msg`Couldn’t load pipeline runs`)}>
            {errorMessage ?? i18n._(msg`Something went wrong. Refresh the page and try again.`)}
          </Alert>
        </Stack>
      </Container>
    )
  }

  if (isLoading && runs.length === 0) {
    return (
      <Container size="xl" py="md" className={classes.page}>
        <Stack gap="lg">
          {header}
          <TableSkeleton />
        </Stack>
      </Container>
    )
  }

  return (
    <Container size="xl" py="md" className={classes.page}>
      <Stack gap="lg">
        {header}

        {runs.length === 0 ? (
          <Center mih={160}>
            <Text c="dimmed" data-testid="pipeline-runs-empty">
              <Trans>No pipeline runs recorded yet.</Trans>
            </Text>
          </Center>
        ) : (
          <div className={classes.layout}>
            <RunsTable runs={runs} selectedRunId={selectedRunId} onSelectRun={onSelectRun} />
            <SelectedRunDetail
              detail={selectedDetail}
              isLoading={isDetailLoading}
              isError={isDetailError}
              errorMessage={detailErrorMessage}
              selectedNodeId={selectedNodeId}
              onSelectNode={onSelectNode}
            />
          </div>
        )}
      </Stack>
    </Container>
  )
}
